/**
 * Scope hierarchy constants and utility functions for the Access Manager.
 *
 * Defines the parent-child relationships between scopes and provides
 * helpers for resolving inheritance, determining effective scopes,
 * and converting between explicit and effective scope sets.
 */

/**
 * Maps parent scopes to their child scopes.
 * When a parent scope is granted, all its children are implicitly inherited.
 */
export const SCOPE_HIERARCHY = {
  supervisor: ["sales", "brand", "diagnosis", "division_performance", "sfe", "efforts", "field_sales", "executive"],
  semantic: ["semantic_query", "thinking"],
};

/**
 * All 14 scopes in display order.
 * Used for rendering the scope tree and iterating over the full scope set.
 */
export const ALL_SCOPES = [
  "admin",
  "supervisor",
  "sales",
  "brand",
  "diagnosis",
  "division_performance",
  "sfe",
  "efforts",
  "field_sales",
  "executive",
  "semantic",
  "semantic_query",
  "thinking",
  "chat",
];

/**
 * Returns the parent scope for a given child scope, or null if the scope
 * has no parent (i.e., it is a top-level scope).
 *
 * @param {string} scope - The scope name to look up.
 * @returns {string|null} The parent scope name, or null.
 */
export function getParentScope(scope) {
  for (const [parent, children] of Object.entries(SCOPE_HIERARCHY)) {
    if (children.includes(scope)) {
      return parent;
    }
  }
  return null;
}

/**
 * Returns true if the given scope is currently inherited because its
 * parent scope is present in the active scopes set.
 *
 * @param {string} scope - The scope to check.
 * @param {string[]} activeScopes - The currently active/granted scopes.
 * @returns {boolean} True if the scope's parent is in activeScopes.
 */
export function isInheritedScope(scope, activeScopes) {
  const parent = getParentScope(scope);
  if (!parent) return false;
  return activeScopes.includes(parent);
}

/**
 * Given a set of explicitly selected/toggled scopes, returns the full
 * effective set including all inherited children of any parent scopes.
 *
 * @param {string[]} selectedScopes - The explicitly selected scopes.
 * @returns {string[]} The effective scopes including inherited children.
 */
export function resolveEffectiveScopes(selectedScopes) {
  const effective = new Set(selectedScopes);

  for (const scope of selectedScopes) {
    const children = SCOPE_HIERARCHY[scope];
    if (children) {
      for (const child of children) {
        effective.add(child);
      }
    }
  }

  return Array.from(effective);
}

/**
 * Given a set of effective (expanded) scopes, returns only the explicitly
 * granted scopes by stripping any children whose parent is also present.
 * This is the inverse of resolveEffectiveScopes — it collapses inherited
 * children back to just their parent.
 *
 * @param {string[]} effectiveScopes - The full effective scope set.
 * @returns {string[]} Only the explicitly granted (non-inherited) scopes.
 */
export function resolveExplicitScopes(effectiveScopes) {
  return effectiveScopes.filter((scope) => {
    const parent = getParentScope(scope);
    if (!parent) return true;
    return !effectiveScopes.includes(parent);
  });
}
